import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import axios from "axios";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { post } from "~/Services";
import { centralizedHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn, renderPublishedColumn } from "~/Utils/TableRenderFunction";
import { usePageDocumentDetails } from "./RepeatType.hooks";

export const RepeatType = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const { data: drilldown, refetch: refetchDocument } = usePageDocumentDetails(id || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    if (query.get("subActiveTab") === "centralizedLandingPageContent") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("subActiveTab")]);

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter(
        (item: any) =>
          String(item.name || "")
            .toLowerCase()
            .includes(searchLower)
        // ||
        // String(item.field_ranking || "")
        //   .toLowerCase()
        //   .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `${centralizedHost}/api/repeat-type`;
    axios
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updated",
      key: "updated",
      // render: renderDateColumn,
      render: (value: any, record: any) => {
        const created = record?.created ? moment(record.created).format("YYYY-MM-DD HH:mm:ss") : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;

        // if both same -> show null for updated
        if (created && updated && created === updated) {
          return null; // you can also return "-" or <Typography.Text>-</Typography.Text>
        }

        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updated"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updated"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/centralized/centralizedPromotion/repeatType/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a, b) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action,
      method,
      module: "Centralized Promotions",
      subModule: "repeatType",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes,
      appList: [{ appName: "CENTRALIZED", apiUrl }],
      payload: data,
      status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        navigate("/centralized/status-information?activeTab=pending");
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteBrandRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${centralizedHost}/api/repeat-type/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        isLoading={isLoading}
        exportFileName="OUTLET"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Name"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "repeatType") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteBrandRequest}
        defaultReviewNotes="Kindly review this Shop & Dine"
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (["name"].includes(sortBy)) {
    const titleA = a[sortBy]?.toLowerCase() || "";
    const titleB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") {
      return titleA < titleB ? 1 : -1;
    } else if (order === "asc") {
      return titleA > titleB ? 1 : -1;
    } else {
      return 0;
    }
  }

  // if (sortBy === "field_level_updated_at") {
  //   const createdA = a.field_level_created_at || null;
  //   const createdB = b.field_level_created_at || null;

  //   if (a[sortBy] === createdA) a[sortBy] = null;
  //   if (b[sortBy] === createdB) b[sortBy] = null;
  // }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
